import { Injectable } from '@angular/core';

/**
 * Business Data Cache Service
 *
 * Provides caching functionality for business-related data to reduce redundant API calls
 * and improve application performance.
 */
@Injectable({
  providedIn: 'root'
})
export class BusinessDataCacheService {
  private cache = new Map<string, {
    data: any;
    timestamp: number;
    expiry: number;
  }>();

  private defaultExpiry = 5 * 60 * 1000; // 5 minutes

  /**
   * Get cached data by key
   * @param key Cache key
   * @returns Cached data or null if not found/expired
   */
  get<T>(key: string): T | null {
    const cached = this.cache.get(key);
    if (!cached) {
      return null;
    }

    // Check if cache has expired
    if (Date.now() > cached.timestamp + cached.expiry) {
      this.cache.delete(key);
      return null;
    }

    console.log(`📦 Cache HIT for key: ${key}`);
    return cached.data;
  }

  /**
   * Set data in cache
   * @param key Cache key
   * @param data Data to cache
   * @param customExpiry Custom expiry time in milliseconds (optional)
   */
  set<T>(key: string, data: T, customExpiry?: number): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      expiry: customExpiry || this.defaultExpiry
    });

    console.log(`📦 Cache SET for key: ${key}, expires in ${(customExpiry || this.defaultExpiry) / 1000}s`);
  }

  /**
   * Clear cache entry or entire cache
   * @param key Optional key to clear specific entry, if not provided clears all
   */
  clear(key?: string): void {
    if (key) {
      this.cache.delete(key);
      console.log(`📦 Cache CLEARED for key: ${key}`);
    } else {
      this.cache.clear();
      console.log(`📦 Cache CLEARED ALL`);
    }
  }

  /**
   * Check if key exists in cache and is not expired
   * @param key Cache key
   * @returns True if key exists and is valid
   */
  has(key: string): boolean {
    const cached = this.cache.get(key);
    if (!cached) {
      return false;
    }

    // Check if expired
    if (Date.now() > cached.timestamp + cached.expiry) {
      this.cache.delete(key);
      return false;
    }

    return true;
  }

  /**
   * Get cache statistics for monitoring
   * @returns Cache statistics object
   */
  getStats() {
    const now = Date.now();
    const entries = Array.from(this.cache.entries());

    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys()),
      expiredEntries: entries.filter(([_, value]) =>
        now > value.timestamp + value.expiry
      ).length,
      totalMemoryUsage: this.estimateMemoryUsage(),
      oldestEntry: entries.length > 0 ?
        Math.min(...entries.map(([_, value]) => value.timestamp)) : null,
      newestEntry: entries.length > 0 ?
        Math.max(...entries.map(([_, value]) => value.timestamp)) : null
    };
  }

  /**
   * Cleanup expired entries
   * @returns Number of entries removed
   */
  cleanup(): number {
    const now = Date.now();
    const initialSize = this.cache.size;

    for (const [key, value] of this.cache.entries()) {
      if (now > value.timestamp + value.expiry) {
        this.cache.delete(key);
      }
    }

    const removedCount = initialSize - this.cache.size;
    if (removedCount > 0) {
      console.log(`📦 Cache CLEANUP: Removed ${removedCount} expired entries`);
    }

    return removedCount;
  }

  /**
   * Estimate memory usage of cache (rough calculation)
   * @returns Estimated memory usage in bytes
   */
  private estimateMemoryUsage(): number {
    let totalSize = 0;

    for (const [key, value] of this.cache.entries()) {
      // Rough estimation: key size + JSON string size of data
      totalSize += key.length * 2; // UTF-16 encoding
      totalSize += JSON.stringify(value.data).length * 2;
      totalSize += 24; // Overhead for timestamp, expiry, object structure
    }

    return totalSize;
  }

  /**
   * Generate cache key for business data
   * @param userId User ID
   * @param type Type of business data (optional)
   * @returns Cache key string
   */
  static generateBusinessKey(userId: string, type: string = 'main'): string {
    return `business-${type}-${userId}`;
  }

  /**
   * Generate cache key for admin data
   * @param userId User ID
   * @returns Cache key string
   */
  static generateAdminKey(userId: string): string {
    return `admin-${userId}`;
  }
}